import React, { useState, useEffect, useRef, useCallback } from "react";

const MAX_RETRIES = 2; // 3 total attempts (initial + 2 retries)
const RETRY_DELAY_MS = 1500;

// Legacy backward-compat only: any Google Drive `uc?export=view` links still stored
// in the DB from before the Imagekit migration get rewritten to a renderable format
// (the raw uc?export=view URL is silently blocked by browsers via a
// Cross-Origin-Resource-Policy header - see Phase B notes). New Imagekit URLs
// (https://ik.imagekit.io/...) and any other URL pass through unchanged.
export const toRenderableUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  const match = url.match(/drive\.google\.com\/uc\?export=view&id=([a-zA-Z0-9_-]+)/);
  if (match) {
    return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w1600`;
  }
  return url;
};

// Imagekit width (px) per `size` prop - drives automatic resizing/compression
// via Imagekit's URL-based transformation params (w-<width>,q-80,f-webp).
const IMAGEKIT_SIZE_WIDTH = {
  thumb: 600,
  hero: 1200,
  slab: 800,
};

/**
 * Appends Imagekit transformation params (?tr=w-<width>,q-80,f-webp) to
 * Imagekit URLs only. Non-Imagekit URLs (e.g. legacy Drive links) and URLs
 * that already carry a `tr=` transformation param are returned unchanged.
 */
export const applyImagekitTransform = (url, size = "thumb") => {
  if (!url || typeof url !== "string") return url;
  if (!url.includes("ik.imagekit.io")) return url;
  if (url.includes("tr=")) return url;
  const width = IMAGEKIT_SIZE_WIDTH[size] || IMAGEKIT_SIZE_WIDTH.thumb;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}tr=w-${width},q-80,f-webp`;
};

/**
 * MarbleImage - reusable image component for all database-driven marble images
 * (Collection tiles, Type hero, Lot cards, Lot cover, Slab gallery).
 *
 * CORE RULE: the <img> tag is ALWAYS present in the DOM from the very first
 * render - it is never conditionally withheld. The dark placeholder is a
 * separate div positioned absolutely BEHIND the image (not instead of it),
 * and is only visually hidden (opacity) once the image has loaded.
 *
 * Behaviour:
 * - Renders <img src={src}> immediately, with the placeholder div behind it.
 * - onLoad -> hides the placeholder (image fades in).
 * - onError -> waits 1500ms, then retries with a cache-busting `?retry=N`
 *   query param appended to the src. Max 2 retries (3 total attempts).
 * - After 2 retries fail, the placeholder (muted-gold "Aurevia" wordmark)
 *   stays visible permanently - never a broken image icon.
 * - Lazy by default; pass `eager` for above-the-fold images (hero, lot cover).
 * - Pass `wrapperClassName` to control aspect ratio / sizing (e.g. "aspect-[16/9] w-full"
 *   to enforce landscape orientation, or "absolute inset-0" inside a fixed-height parent).
 * - Pass `size` ("thumb" | "hero" | "slab", default "thumb") to control the Imagekit
 *   resize/compression width applied via ?tr=w-<width>,q-80,f-webp (Imagekit URLs only).
 */
const MarbleImage = ({
  src,
  alt = "",
  className = "",
  wrapperClassName = "",
  eager = false,
  fill = true,
  size = "thumb",
  style,
  ...rest
}) => {
  const baseSrc = applyImagekitTransform(toRenderableUrl(src), size) || "";
  const [retryCount, setRetryCount] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const retryCountRef = useRef(0);
  const timeoutRef = useRef(null);
  const prevBaseSrcRef = useRef(null);

  // Reset all state whenever the underlying src actually changes (e.g. navigating
  // between lots). Guard against re-renders that carry the same baseSrc (e.g. a
  // parent grid re-rendering while this image is still mid-load) - in that case
  // skip the reset entirely so an in-flight image load isn't wiped/restarted.
  useEffect(() => {
    if (prevBaseSrcRef.current === baseSrc) {
      return;
    }
    prevBaseSrcRef.current = baseSrc;
    retryCountRef.current = 0;
    setRetryCount(0);
    setHasError(false);
    setHasLoaded(false);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [baseSrc]);

  const handleLoad = () => {
    setHasLoaded(true);
  };

  // Callback ref: if the img is already `complete` when it mounts (e.g. the
  // browser served it straight from cache), mark it loaded immediately
  // instead of waiting for the onLoad event - avoids a one-render-cycle
  // opacity-0 flash of the placeholder on cached image loads.
  const handleImgRef = useCallback((node) => {
    if (node && node.complete && node.naturalWidth > 0) {
      setHasLoaded(true);
    }
  }, []);

  const handleError = () => {
    if (retryCountRef.current >= MAX_RETRIES) {
      setHasError(true);
      return;
    }
    // Silent retry - wait 1500ms, then bump retryCount which changes the src
    // (cache-busting ?retry=N), triggering a fresh load attempt. The
    // placeholder stays visible throughout - no spinner, no error flash.
    timeoutRef.current = setTimeout(() => {
      retryCountRef.current += 1;
      setRetryCount(retryCountRef.current);
    }, RETRY_DELAY_MS);
  };

  const fillClasses = fill ? "absolute inset-0 h-full w-full object-cover" : "";
  const finalSrc =
    retryCount === 0 ? baseSrc : `${baseSrc}${baseSrc.includes("?") ? "&" : "?"}retry=${retryCount}`;

  return (
    <div className={`overflow-hidden bg-brand-ink ${wrapperClassName}`}>
      {/* Placeholder - positioned behind the image, visible until loaded, permanent on error */}
      <div
        className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
          hasLoaded && !hasError ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
      >
        <span className="font-serif text-brand-gold/35 text-xs md:text-sm uppercase tracking-[0.3em]">
          Aurevia
        </span>
      </div>

      {/* Image - ALWAYS rendered, never conditionally withheld */}
      <img
        key={retryCount}
        ref={handleImgRef}
        src={finalSrc}
        alt={alt}
        style={style}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        onLoad={handleLoad}
        onError={handleError}
        className={`${fillClasses} transition-opacity duration-500 ${
          hasLoaded ? "opacity-100" : "opacity-0"
        } ${className}`}
        {...rest}
      />
    </div>
  );
};

export default MarbleImage;
